import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@api";
import { useAuth } from "../../../context/AuthContext";
import FriendRequestModal from "../Profile/FriendRequestModal.jsx"
import './DirectoryAuthMenu.css'
import { MessageCircle } from "lucide-react";

function DirectoryAuthMenu() {
  const { checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [publicUser, setPublicUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [myLeagues, setMyLeagues] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [friendToast, setFriendToast] = useState(null);
  const [friendRequestModal, setFriendRequestModal] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const menuRef = useRef(null);

  useEffect(() => {
    const loadPublicAuth = async () => {
      try {
        const { data } = await api.get("/api/auth/public", { withCredentials: true });
        setIsAuthenticated(data?.authenticated || false);
        setPublicUser(data?.user || null);
      } catch (error) {
        setIsAuthenticated(false);
        setPublicUser(null);
      }
    };

    loadPublicAuth();
  }, []);

  useEffect(() => {
    const fetchUnreadMessages = async () => {
      if (!isAuthenticated) return;

      try {
        const res = await api.get("/api/messages/unread-count");

        setUnreadMessages(res.data?.unread_count || 0);
      } catch (error) {
        console.error("Failed to fetch unread messages:", error);
      }
    };

    fetchUnreadMessages();

    window.addEventListener(
      "refresh-message-count",
      fetchUnreadMessages
    );

    return () => {
      window.removeEventListener(
        "refresh-message-count",
        fetchUnreadMessages
      );
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchMyProfile = async () => {
      if (!isAuthenticated) {
        setProfileId(null);
        return;
      }

      try {
        const res = await api.get("/api/user-profiles-v2/me", {
          withCredentials: true,
        });

        setProfileId(res.data.profile?.id || null);
      } catch (error) {
        console.error("Failed to fetch profile:", error);
        setProfileId(null);
      }
    };

    fetchMyProfile();
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchMyLeagues = async () => {
      if (!isAuthenticated) return;

      try {
        const res = await api.get("/api/directory/my-leagues");
        setMyLeagues(res.data || []);
      } catch (e) {
        console.error("Failed to fetch my leagues", e);
      }
    };

    fetchMyLeagues();
  }, [isAuthenticated]);


  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isAuthenticated) return;

      try {
        const [listRes, countRes] = await Promise.all([
          api.get("/api/notifications"),
          api.get("/api/notifications/unread-count"),
        ]);

        setNotifications(listRes.data || []);
        setUnreadCount(countRes.data?.unread || 0);
      } catch (err) {
        console.error("Failed to fetch notifications", err);
      }
    };

    fetchNotifications();
  }, [isAuthenticated]);


  useEffect(() => {
    const refreshNotifications = async () => {
      try {
        const [listRes, countRes] = await Promise.all([
          api.get("/api/notifications"),
          api.get("/api/notifications/unread-count"),
        ]);

        setNotifications(listRes.data || []);
        setUnreadCount(countRes.data?.unread || 0);
      } catch (err) {
        console.error("Failed to refresh notifications", err);
      }
    };

    window.addEventListener("refresh-notifications", refreshNotifications);

    return () => {
      window.removeEventListener("refresh-notifications", refreshNotifications);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleFriendAccepted = (event) => {
      setFriendToast(
        `You and ${event.detail.name} are now friends.`
      );

      setTimeout(() => {
        setFriendToast(null);
      }, 3000);
    };

    window.addEventListener("friend-accepted", handleFriendAccepted);

    return () => {
      window.removeEventListener(
        "friend-accepted",
        handleFriendAccepted
      );
    };
  }, []);


  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  const toggleNotifications = async () => {
    const opening = !showNotifications;
    setShowNotifications(opening);

    if (opening) {
      const unread = notifications.filter(n => !n.is_read);

      for (const n of unread) {
        await api.post(`/api/notifications/${n.id}/read`);
      }

      const [listRes, countRes] = await Promise.all([
        api.get("/api/notifications"),
        api.get("/api/notifications/unread-count"),
      ]);

      setNotifications(listRes.data || []);
      setUnreadCount(countRes.data?.unread || 0);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="directory-user-menu" ref={menuRef}>
        <button
          type="button"
          className="directory-mobile-menu-btn"
          onClick={() => setShowUserMenu((prev) => !prev)}
          aria-label="Open navigation menu"
        >
          ☰
        </button>

        <button
          className="directory-login-btn"
          onClick={() => navigate("/login")}
        >
          Login
        </button>

        {showUserMenu && (
          <div className="directory-user-dropdown directory-guest-dropdown">
            <div
              className="directory-user-item"
              onClick={() => {
                navigate("/");
                setShowUserMenu(false);
              }}
            >
              Find Sports
            </div>

            <div
              className="directory-user-item"
              onClick={() => {
                navigate("/field");
                setShowUserMenu(false);
              }}
            >
              The Arena
            </div>

            <div className="directory-user-divider" />

            <div
              className="directory-user-item"
              onClick={() => {
                navigate("/login");
                setShowUserMenu(false);
              }}
            >
              Login
            </div>
          </div>
        )}
      </div>
    );
  }


  const approvedClaim = myLeagues.find((league) => league.status === "approved");
  const handleNotificationClick = async (notification) => {
    if (notification.type === "friend_request") {
      try {
        const res = await api.get(
          `/api/user-profiles-v2/${notification.actor_profile_id}`
        );

        setShowNotifications(false);

        setFriendRequestModal({
          notification,
          requester: res.data.profile,
        });
      } catch (error) {
        console.error("Failed to load friend requester:", error);
      }

      return;
    }

    if (notification.type === "friend_request_accepted") {
      setShowNotifications(false);
      navigate(`/profile/${notification.actor_profile_id}`);
    }
  };





  return (
    <div className="directory-user-menu" ref={menuRef}>
      <div className={`notification-wrapper ${showNotifications ? "open" : ""}`}>
        <div
          className="notification-bell"
          onClick={toggleNotifications}
        >
          🔔
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount}
            </span>
          )}
          <div className="notification-tooltip">
            Notifications
          </div>
        </div>

        {showNotifications && (
          <div className="notification-dropdown">
            {notifications.length === 0 && (
              <div className="notification-empty">
                No notifications
              </div>
            )}

            {notifications.map((n) => (
              <div
                key={n.id}
                className={`notification-item ${n.is_read ? "" : "unread"}`}
                onClick={() => handleNotificationClick(n)}
              >

                <div className="notification-row">
                  <span className="notification-icon">
                    {n.type === "claim_approved" && "✅"}
                    {n.type === "claim_rejected" && "❌"}
                    {n.type === "claim_pending" && "⏳"}
                    {n.type === "league_unclaimed" && "⚠️"}
                    {n.type === "friend_request" && "👥"}
                    {n.type === "friend_request_accepted" && "✅"}
                  </span>

                  <div className="notification-content">
                    <div className="notification-message">
                      {n.message}
                    </div>

                    <div className="notification-time">
                      {new Date(n.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <button
                    className="notification-close"
                    onClick={async (e) => {
                      e.stopPropagation();

                      await api.delete(`/api/notifications/${n.id}`);

                      const [listRes, countRes] = await Promise.all([
                        api.get("/api/notifications"),
                        api.get("/api/notifications/unread-count"),
                      ]);

                      setNotifications(listRes.data || []);
                      setUnreadCount(countRes.data?.unread || 0);
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        className="directory-message-btn"
        onClick={() => navigate("/messages")}
        aria-label="Messages"
      >
        <MessageCircle size={20} />

        {unreadMessages > 0 && (
          <span className="message-badge">
            {unreadMessages}
          </span>
        )}
      </button>


      <button
        type="button"
        className={`directory-user-avatar-btn ${showUserMenu ? "open" : ""}`}
        onClick={() => setShowUserMenu((prev) => !prev)}
        aria-label="Open account menu"
      >
        {publicUser?.profile_image ? (
          <img
            src={publicUser.profile_image}
            alt={publicUser?.username || "Account"}
            className="directory-user-avatar-img"
          />
        ) : (
          <span className="directory-user-avatar-fallback">
            {(publicUser?.username || "A")
              .trim()
              .slice(0, 2)
              .toUpperCase()}
          </span>
        )}
      </button>

      {showUserMenu && (
        <div className="directory-user-dropdown">

          {/* MOBILE-ONLY DIRECTORY NAV */}
          <div className="directory-mobile-nav">

            <div
              className="directory-user-item"
              onClick={() => {
                navigate("/sport");
                setShowUserMenu(false);
              }}
            >
              Browse Sports
            </div>

            <div
              className="directory-user-item"
              onClick={() => {
                navigate("/field");
                setShowUserMenu(false);
              }}
            >
              The Arena
            </div>

            {profileId && (
              <div
                className="directory-user-item"
                onClick={() => {
                  navigate(`/profile/${profileId}`);
                  setShowUserMenu(false);
                }}
              >
                My Profile
              </div>
            )}

            <div className="directory-user-divider" />
          </div>

          {(publicUser?.organization_id || approvedClaim) && (
            <>
              <div
                className="directory-user-item"
                onClick={async () => {
                  await checkAuth();
                  navigate("/dashboard/seasons");
                  setShowUserMenu(false);
                }}
              >
                My Account
              </div>

              <div className="directory-user-divider" />
            </>
          )}

          <div
            className="directory-user-item"
            onClick={handleLogout}
          >
            Logout
          </div>

        </div>
      )}
      {friendRequestModal && (
        <FriendRequestModal
          requester={friendRequestModal.requester}
          onAccept={async () => {
            try {
              await api.patch(
                `/api/friends/${friendRequestModal.notification.related_id}/accept`
              );
              const friendName =
                `${friendRequestModal.requester.first_name || ""} ${friendRequestModal.requester.last_name || ""
                  }`.trim() ||
                friendRequestModal.requester.display_name ||
                "this person";

              setFriendToast(`You and ${friendName} are now friends.`);

              setTimeout(() => {
                setFriendToast(null);
              }, 3000);
              window.dispatchEvent(new Event("friend-status-changed"));

              setFriendRequestModal(null);

              const [listRes, countRes] = await Promise.all([
                api.get("/api/notifications"),
                api.get("/api/notifications/unread-count"),
              ]);

              setNotifications(listRes.data || []);
              setUnreadCount(countRes.data?.unread || 0);
            } catch (error) {
              console.error("Failed to accept friend request:", error);
            }
          }}
          onDecline={async () => {
            try {
              await api.patch(
                `/api/friends/${friendRequestModal.notification.related_id}/decline`
              );

              setFriendRequestModal(null);

              const [listRes, countRes] = await Promise.all([
                api.get("/api/notifications"),
                api.get("/api/notifications/unread-count"),
              ]);

              setNotifications(listRes.data || []);
              setUnreadCount(countRes.data?.unread || 0);
            } catch (error) {
              console.error("Failed to decline friend request:", error);
            }
          }}
          onViewProfile={() => {
            navigate(
              `/profile/${friendRequestModal.notification.actor_profile_id}`
            );
            setFriendRequestModal(null);
          }}
          onClose={() => setFriendRequestModal(null)}
        />
      )}
      {friendToast && (
        <div className="friend-toast">
          ✓ {friendToast}
        </div>
      )}
    </div>

  );
}

export default DirectoryAuthMenu;